"use client";

import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, Mail, Phone, Lock, Store, Shield, Edit, Trash, Users, Building2, AlertTriangle } from "lucide-react";

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    email: "",
    phone: "",
    password: "",
    businessName: "",
    role: "USER",
    storeId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await api.get("/stores", {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        const storesData = Array.isArray(res.data) ? res.data : [];
        setStores(storesData);
      } catch (err) {
        console.error("Fetch stores error:", err);
        setError("⚠ Failed to fetch stores");
      }
    };
    fetchStores();
  }, []);

  // Fetch users (initially all, or by store when selected)
  const fetchUsers = async (storeId = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      let url = "/users";
      if (storeId) {
        url = `/users/by-store/${storeId}`;
      }
      const res = await api.get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch users error:", err);
      setError("⚠ Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleStoreChange = (e) => {
    const storeId = e.target.value;
    setSelectedStoreId(storeId);
    fetchUsers(storeId);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openModal = (userToEdit = null) => {
    if (userToEdit) {
      setFormData({
        id: userToEdit.id,
        name: userToEdit.name,
        email: userToEdit.email,
        phone: userToEdit.phone,
        password: "", // Password not fetched, so empty for edit (assume not changing unless provided)
        businessName: userToEdit.businessName || "",
        role: userToEdit.role,
        storeId: userToEdit.storeIds?.[0] || "", // Assuming single store for simplicity
      });
      setIsEditMode(true);
    } else {
      setFormData({
        id: null,
        name: "",
        email: "",
        phone: "",
        password: "",
        businessName: "",
        role: "USER",
        storeId: "",
      });
      setIsEditMode(false);
    }
    setIsModalOpen(true);
    setError("");
    setSuccess("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDeleteConfirmId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");

      if (!formData.storeId) {
        setError("⚠ Please select a store");
        setLoading(false);
        return;
      }

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password || null, // Allow null if not changing
        businessName: formData.businessName || null,
        role: formData.role,
      };

      let res;
      if (isEditMode) {
        // For update, include storeIds as array
        payload.storeIds = [parseInt(formData.storeId)];
        res = await api.put(`/users/${formData.id}`, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        setUsers(users.map((u) => (u.id === formData.id ? res.data : u)));
        setSuccess("🎉 User updated successfully!");
      } else {
        res = await api.post(
          `/users/${formData.storeId}/create`,
          payload,
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        setUsers([...users, res.data]);
        setSuccess("🎉 User created successfully!");
      }

      closeModal();
      fetchUsers(selectedStoreId); // Refresh list
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} user error:`, err);
      setError(err.response?.data?.message || `⚠ Failed to ${isEditMode ? "update" : "create"} user`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/users/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setUsers(users.filter((u) => u.id !== id));
      setSuccess("🗑 User deleted successfully!");
    } catch (err) {
      console.error("Delete user error:", err);
      setError(err.response?.data?.message || "⚠ Failed to delete user");
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-purple-100 dark:from-gray-900 dark:via-gray-800 dark:to-black p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-100 dark:border-gray-800"
      >
        <h1 className="text-4xl font-extrabold mb-8 text-center flex items-center justify-center gap-3 text-indigo-600 dark:text-indigo-400">
          <Users className="h-8 w-8" /> User Management
        </h1>

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 p-4 rounded-xl bg-red-100 text-red-700 font-medium shadow-md flex items-center gap-2"
          >
            <AlertTriangle className="h-5 w-5" /> {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 p-4 rounded-xl bg-green-100 text-green-700 font-medium shadow-md"
          >
            {success}
          </motion.div>
        )}

        {/* Filter by Store */}
        <div className="mb-6 flex items-center gap-4">
          <select
            value={selectedStoreId}
            onChange={handleStoreChange}
            className="w-64 pl-10 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
          >
            <option value="">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
          <Store className="h-5 w-5 text-indigo-500 absolute left-4" />
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-indigo-50 dark:bg-indigo-900/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Business Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Store IDs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <motion.tr
                    key={u.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.businessName || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.role}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.storeIds?.join(", ") || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openModal(u)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(u.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash className="h-5 w-5" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create User Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => openModal()}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold shadow-lg transition flex items-center justify-center gap-2"
        >
          <UserPlus className="h-6 w-6" /> Create New User
        </motion.button>
      </motion.div>

      {/* Modal for Create/Edit */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-100 dark:border-gray-800"
            >
              <h2 className="text-3xl font-extrabold mb-6 text-center flex items-center justify-center gap-3 text-indigo-600 dark:text-indigo-400">
                {isEditMode ? <Edit className="h-7 w-7" /> : <UserPlus className="h-7 w-7" />} {isEditMode ? "Edit User" : "Create New User"}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  />
                  <UserPlus className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Email */}
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  />
                  <Mail className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Phone */}
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  />
                  <Phone className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Password */}
                <div className="relative">
                  <input
                    type="password"
                    name="password"
                    placeholder={isEditMode ? "New Password (optional)" : "Password"}
                    value={formData.password}
                    onChange={handleChange}
                    required={!isEditMode}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  />
                  <Lock className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Business Name */}
                <div className="relative">
                  <input
                    type="text"
                    name="businessName"
                    placeholder="Business Name (optional)"
                    value={formData.businessName}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  />
                  <Building2 className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Role */}
                <div className="relative">
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  >
                    <option value="USER">User</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                  <Shield className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Store */}
                <div className="relative">
                  <select
                    name="storeId"
                    value={formData.storeId}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  >
                    <option value="">Select Store</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                  <Store className="absolute left-4 top-3.5 h-5 w-5 text-indigo-500" />
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold shadow-lg transition disabled:opacity-50"
                  >
                    {loading ? "Saving..." : isEditMode ? "🚀 Update User" : "🚀 Create User"}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-3 rounded-2xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-bold shadow-lg transition"
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-100 dark:border-gray-800"
            >
              <h2 className="text-2xl font-bold mb-4 text-center text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                <Trash className="h-6 w-6" /> Confirm Delete
              </h2>
              <p className="text-center mb-6 text-gray-700 dark:text-gray-300">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleDelete(deleteConfirmId)}
                  disabled={loading}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg transition disabled:opacity-50"
                >
                  {loading ? "Deleting..." : "🗑 Delete"}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={closeModal}
                  className="flex-1 py-3 rounded-2xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-bold shadow-lg transition"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;